import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';

export enum AssureurSanteName {
  DEUX_MA = 'DEUX_MA',
  AESIO_MUTUELLE = 'AESIO_MUTUELLE',
  ALPTIS = 'ALPTIS',
  VERALTY_APICIL = 'VERALTY_APICIL',
  APIVIA = 'APIVIA',
  APRIL = 'APRIL',
  ASAF_AFPS = 'ASAF_AFPS',
  ECA = 'ECA',
  ENTORIA = 'ENTORIA',
  GENERALI = 'GENERALI',
  HARMONIE_MUTUELLE = 'HARMONIE_MUTUELLE',
  HENNER = 'HENNER',
  MALAKOFF_MEDERIC_AMIS = 'MALAKOFF_MEDERIC_AMIS',
  MALAKOFF_MEDERIC = 'MALAKOFF_MEDERIC',
  NEOLIANE = 'NEOLIANE',
  SOLLY_AZAR = 'SOLLY_AZAR',
  SPVIE = 'SPVIE',
  SWISSLIFE = 'SWISSLIFE',
  UTWIN = 'UTWIN',
  ZENIOO = 'ZENIOO'
}

export const ASSUREUR_DISPLAY_NAMES: Record<AssureurSanteName, string> = {
  [AssureurSanteName.DEUX_MA]: '2MA',
  [AssureurSanteName.AESIO_MUTUELLE]: 'AESIO MUTUELLE',
  [AssureurSanteName.ALPTIS]: 'ALPTIS',
  [AssureurSanteName.VERALTY_APICIL]: 'VERALTY / APICIL',
  [AssureurSanteName.APIVIA]: 'APIVIA',
  [AssureurSanteName.APRIL]: 'APRIL',
  [AssureurSanteName.ASAF_AFPS]: 'ASAF&AFPS',
  [AssureurSanteName.ECA]: 'ECA',
  [AssureurSanteName.ENTORIA]: 'ENTORIA',
  [AssureurSanteName.GENERALI]: 'GENERALI',
  [AssureurSanteName.HARMONIE_MUTUELLE]: 'HARMONIE MUTUELLE',
  [AssureurSanteName.HENNER]: 'HENNER',
  [AssureurSanteName.MALAKOFF_MEDERIC_AMIS]: 'MALAKOFF MEDERIC AMIS',
  [AssureurSanteName.MALAKOFF_MEDERIC]: 'MALAKOFF MEDERIC',
  [AssureurSanteName.NEOLIANE]: 'NEOLIANE',
  [AssureurSanteName.SOLLY_AZAR]: 'SOLLY AZAR',
  [AssureurSanteName.SPVIE]: 'SPVIE',
  [AssureurSanteName.SWISSLIFE]: 'SWISSLIFE',
  [AssureurSanteName.UTWIN]: 'UTWIN',
  [AssureurSanteName.ZENIOO]: 'ZENIOO'
};

export interface AssureurSanteCredentialResponse {
  id: number;
  userId: number;
  userEmail: string;
  userName: string;
  assureurName: AssureurSanteName;
  assureurDisplayName: string;
  login: string;
  password: string;
  codeCourtier: string;
}

export interface AssureurSanteCredentialCreate {
  userId: number;
  assureurName: AssureurSanteName;
  login: string;
  password: string;
  codeCourtier?: string;
}

export interface AssureurSanteCredentialUpdate {
  login?: string;
  password?: string;
  codeCourtier?: string;
}

export interface BulkCreateRequest {
  userId: number;
  credentials: {
    assureurName: AssureurSanteName;
    login: string;
    password: string;
    codeCourtier?: string;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class AssureurSanteCredentialsService {

  private apiUrl = `${environment.casApiUrl}/api/v1/assureur-sante-credentials`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  getAssureurNames(): Observable<{ name: string; displayName: string }[]> {
    return this.http.get<{ name: string; displayName: string }[]>(
      `${this.apiUrl}/assureurs`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  getAllCredentials(): Observable<AssureurSanteCredentialResponse[]> {
    return this.http.get<AssureurSanteCredentialResponse[]>(
      this.apiUrl,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  getCredentialsByUserId(userId: number): Observable<AssureurSanteCredentialResponse[]> {
    return this.http.get<AssureurSanteCredentialResponse[]>(
      `${this.apiUrl}/user/${userId}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  getMyCredentials(): Observable<AssureurSanteCredentialResponse[]> {
    return this.http.get<AssureurSanteCredentialResponse[]>(
      `${this.apiUrl}/me`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  getMyCredentialForAssureur(assureurName: AssureurSanteName): Observable<AssureurSanteCredentialResponse> {
    return this.http.get<AssureurSanteCredentialResponse>(
      `${this.apiUrl}/me/${assureurName}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  createCredential(data: AssureurSanteCredentialCreate): Observable<AssureurSanteCredentialResponse> {
    return this.http.post<AssureurSanteCredentialResponse>(
      this.apiUrl,
      data,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  bulkCreate(data: BulkCreateRequest): Observable<AssureurSanteCredentialResponse[]> {
    return this.http.post<AssureurSanteCredentialResponse[]>(
      `${this.apiUrl}/bulk`,
      data,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  updateCredential(credentialId: number, data: AssureurSanteCredentialUpdate): Observable<AssureurSanteCredentialResponse> {
    return this.http.put<AssureurSanteCredentialResponse>(
      `${this.apiUrl}/${credentialId}`,
      data,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  deleteCredential(credentialId: number): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/${credentialId}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }

  seedCredentials(): Observable<{ message: string; count: number }> {
    return this.http.post<{ message: string; count: number }>(
      `${this.apiUrl}/seed`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(e => throwError(() => e)));
  }
}
